import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MailReminder, MailReminderStrategy } from '../../models/mailReminder';
import { Divider, Chip } from '@mui/material';
import { DeleteIcon } from '../../components/buttons';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowParams, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { StandardDialog } from '../../components/dialogs/base';
import Form, { FormRef } from './components/form';
import { toast } from '../../utils/toast';
import { useDeleteDialog } from '../../hooks';
import Header from './components/header';
import { apiDelete, apiGet, apiPost, showErrorToast } from '../../services/apiClient';
import { toDateInputValue } from '../../utils/misc';
import { useTranslation } from 'react-i18next';

const MailReminderPage: React.FC = () => {
  const { t } = useTranslation();

  const [mailReminders, setMailReminders] = useState<MailReminder[]>([]);
  const [query, setQuery] = useState<Record<string, string>>({});

  const mergeQuery = useCallback((partial: Record<string, string | undefined>) => {
    setQuery(prev => {
      const next: Record<string, string> = { ...prev };
      for (const [k, v] of Object.entries(partial)) {
        if (v !== undefined) next[k] = v;
      }
      return next;
    });
  }, []);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<MailReminder> & { content?: string }>({
    active: true
  });
  const [action, setAction] = useState<'create' | 'update'>('create');
  const { DeleteModal, confirmDelete } = useDeleteDialog();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 30
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({ id: false });
  const formRef = useRef<FormRef>(null);

  const fetchMailReminders = useCallback(
    async () => {
      try {
        setLoading(true);
        const { page, pageSize } = paginationModel;
        const resp = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/mail_reminders`, {
          ...query,
          offset: page * pageSize,
          limit: pageSize,
        });
        const { result, count } = await resp.json();
        result.forEach((item: MailReminder) => {
          item.firstSentNotBefore = toDateInputValue(item.firstSentNotBefore);
          item.lastSentNotAfter = item.lastSentNotAfter ? toDateInputValue(item.lastSentNotAfter) : null;
        });
        setMailReminders(result);
        setRowCount(count);
      } catch (error) {
        showErrorToast(error, t('Failed to fetch mail reminders'));
      } finally {
        setLoading(false);
      }
    },
    [query, paginationModel]
  );

  useEffect(() => {
    fetchMailReminders();
  }, [fetchMailReminders, query, paginationModel]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const url = action === 'create'
        ? `${process.env.REACT_APP_API_URL}/api/v1/mail_reminders`
        : `${process.env.REACT_APP_API_URL}/api/v1/mail_reminder/${formData.id}`;

      await apiPost(url, {
        strategy: formData.strategy,
        active: formData.active,
        email_to: formData.emailTo,
        email_cc: formData.emailCc,
        email_bcc: formData.emailBcc,
        email_subject: formData.emailSubject,
        email_body: formData.emailBody,
        content: formData.content,
        first_sent_not_before: formData.firstSentNotBefore ? new Date(formData.firstSentNotBefore).getTime() : undefined,
        last_sent_not_after: formData.lastSentNotAfter ? new Date(formData.lastSentNotAfter).getTime() : undefined,
        email_reply_to: formData.emailReplyTo,
        email_body_generation_prompt: formData.emailBodyGenerationPrompt,
      });
      toast.success(`${t('Mail reminder')} ${action === 'create' ? t('created') : t('updated')}!`);
      setFormData({});
      setIsOpen(false);
      await fetchMailReminders();
    } catch (error) {
      showErrorToast(error, t('Something went wrong!'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    const mailReminder = mailReminders.find(item => item.id === params.id);
    if (mailReminder) {
      setAction('update');
      setFormData({ ...mailReminder, id: `${params.id}` });
      setIsOpen(true);
    }
  };

  const renderActiveCell = ({ value }: GridRenderCellParams) => (
    value
      ? <Chip label={t('Active')} color='success' />
      : <Chip label={t('Not Active')} color='default' />
  );

  const renderStrategyCell = ({ value }: GridRenderCellParams) => (
    Object.values(MailReminderStrategy)[value]
  );

  const handleDelete = async (id: string | number) => {
    const mailReminder = mailReminders.find(item => item.id === id);
    const ok = await confirmDelete(mailReminder?.emailSubject);
    if (!ok) return;
    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/v1/mail_reminder/${id}`);
      toast.success(t('Successfully deleted!'));
      await fetchMailReminders();
    } catch (error) {
      showErrorToast(error, t('Email reminder delete failed!'));
    }
  };

  const renderActionCell = ({ id }: GridRenderCellParams) => (
    <DeleteIcon onClick={e => { e.stopPropagation(); handleDelete(id); }} />
  );

  const columns: GridColDef[] = [
    { field: 'id', headerName: t('ID'), width: 50 },
    { field: 'emailSubject', headerName: t('Email subject'), flex: 1 },
    { field: 'strategy', headerName: t('Strategy'), flex: 1, renderCell: renderStrategyCell },
    { field: 'emailTo', headerName: t('Email to'), flex: 1 },
    { field: 'lastSentAt', headerName: t('Last sent at'), width: 150 },
    { field: 'active', headerName: t('Active'), width: 150, renderCell: renderActiveCell },
    { field: 'actions', headerName: t('Actions'), width: 150, renderCell: renderActionCell }
  ];

  return (
    <>
      <Header
        setIsOpen={setIsOpen}
        handleQuery={mergeQuery}
      />

      <Divider />

      <DataGrid
        rows={mailReminders}
        columns={columns}
        onRowClick={params => handleRowClick(params)}
        pagination
        paginationMode='server'
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        rowCount={rowCount}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        autoHeight
        loading={loading}
        localeText={{
          noRowsLabel: t('No mail reminders found'),
          noResultsOverlayLabel: t('No mail reminders found'),
        }}
      />

      {/* modal */}
      <StandardDialog
        open={isOpen}
        onClose={() => { setIsOpen(false); setFormData({}); setAction('create'); }}
        title={action === 'create' ? t('Add Mail Reminder') : t('Update Mail Reminder')}
        maxWidth="md"
        width={{ xs: '100%', sm: '90%', md: '700px', lg: '750px' }}
        height={{ xs: '100vh', sm: '95vh', md: '90vh', lg: '85vh' }}
        primaryAction={{
          label: submitting
            ? (action === 'create' ? t('Adding...') : t('Updating...'))
            : (action === 'create' ? t('Add Mail Reminder') : t('Update Mail Reminder')),
          onClick: async () => {
            if (formRef.current) {
              await formRef.current.submit();
            }
          },
          loading: submitting,
          disabled: submitting
        }}
        secondaryAction={{
          label: t('Cancel'),
          onClick: () => { setIsOpen(false); setFormData({}); setAction('create'); }
        }}
        contentSx={{ paddingX: { xs: 1.5, sm: 2, md: 2 } }}
      >
        <Form
          ref={formRef}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          actionText=""
        />
      </StandardDialog>
      {DeleteModal}
    </>
  );
};

export default MailReminderPage;
